const ffCartSectionsMap = (() => {
  if (document.querySelector(".ff-cart-drawer")) {
    return {
      cartApiIds: [
        {
          sectionApiId: "ff-cart-btn",
          selectors: [".ff-cart-count-number--js"],
        },
        {
          sectionApiId: "ff-cart-drawer",
          selectors: [".ff-cart-drawer_content--js"],
        },
      ],
      crossSellingApiId: {
        sectionApiId: "ff-cross-selling",
        selector: ".ff-cross-selling--js",
      },
    };
  } else {
    const result = {
      cartApiIds: [
        {
          sectionApiId: "ff-cart-btn",
          selectors: [".ff-cart-count-number--js"],
        },
        {
          sectionApiId: "ff-main-cart-items",
          selectors: [".ff-main-cart-items-section--js"],
        },
        {
          sectionApiId: "ff-main-cart-footer",
          selectors: [
            ".ff-cart-footer-section--js",
            ".ff-cart-footer-checkout-button-container--js",
          ],
        },
      ],
      crossSellingApiId: {
        sectionApiId: "ff-cross-selling",
        selector: ".ff-cross-selling--js",
      },
    };

    result.cartApiIds.forEach((apiId) => {
      apiId.selectors.forEach((selector) => {
        const el = document.querySelector(selector);

        el.querySelectorAll("style").forEach((style) => {
          document.body.appendChild(style);
        });
      });
    });

    return result;
  }
})();

ffCartSectionsMap.cartApiIds.forEach(
  ({ sectionApiId, selectors, onUpdate }) => {
    selectors.forEach((selector) => {
      const el = document.querySelector(selector);

      if (!el) return;
      ffAttachEvents(el);
    });
  }
);

const ffDebounce = (fn, delay = 300) => {
  let timerId;
  let rejectFn;

  async function debounced(...args) {
    if (timerId) {
      clearTimeout(timerId);
      rejectFn?.("Debounce cancelled");
    }

    return new Promise(async (resolve, reject) => {
      rejectFn = reject;
      timerId = setTimeout(async () => {
        timerId = null;
        try {
          const result = await fn(...args);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  }

  return debounced;
};
const withLock = (fn) => {
  let locked = false;
  return async (...args) => {
    if (locked) throw new Error("Function is locked");
    locked = true;
    try {
      return await fn(...args);
    } finally {
      locked = false;
    }
  };
};

function prepareBody(
  input,
  sectionIds = ffCartSectionsMap.cartApiIds.map(
    ({ sectionApiId }) => sectionApiId
  )
) {
  const sectionsField = { sections: sectionIds.join(",") };

  if (input instanceof HTMLFormElement) {
    const formData = new FormData(input);
    Object.entries(sectionsField).forEach(([k, v]) => formData.append(k, v));
    return formData;
  }

  if (typeof input === "string") {
    const form = document.querySelector(input);
    const formData = new FormData(form);
    Object.entries(sectionsField).forEach(([k, v]) => formData.append(k, v));
    return formData;
  }

  return JSON.stringify({ ...input, ...sectionsField });
}

async function fetchBundled(url, options = {}) {
  const headers = { ...options.headers };
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(`${window.Shopify.routes.root}${url}`, {
    ...options,
    headers,
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

async function _add(payload, sectionIds) {
  const body = prepareBody(payload, sectionIds);
  const response = await fetchBundled("cart/add.js", { method: "POST", body });
  return { ...(await _fetch()), sections: response.sections };
}

async function _update(payload, sectionIds) {
  const body = prepareBody(payload, sectionIds);
  return fetchBundled("cart/update.js", { method: "POST", body });
}

async function _clear(sectionIds) {
  const body = prepareBody({}, sectionIds);
  return fetchBundled("cart/clear.js", { method: "POST", body });
}

async function _fetch() {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`);
  return response.json();
}

// --- Locking + Debouncing ---
const add = withLock(_add);
const update = withLock(_update);
const clear = withLock(_clear);

const ffDebouncedAdd = ffDebounce(add, 300);
const ffDebouncedUpdate = ffDebounce(update, 300);
const ffDebouncedClear = ffDebounce(clear, 300);

// --- Cross-Selling logic ---
async function ffGenerateCrossSelling({
  productIdArray,
  sectionId,
  limit = 8,
  intent = "related",
}) {
  try {
    const url = `${window["Shopify"].routes.root}recommendations/products`;
    const computedLimit = Math.ceil(limit / productIdArray.length);

    const fetchPromises = productIdArray.map((productId) => {
      const params = new URLSearchParams({
        section_id: sectionId,
        product_id: productId,
        limit: computedLimit > 3 ? computedLimit : 3,
        intent: intent,
      });

      return fetch(`${url}?${params}`).then((res) => res.text());
    });

    const results = await Promise.all(fetchPromises);

    // Combine all HTML pieces
    const combinedHTML = results.join("");

    FfEvents.emit("crossSelling:generated", { html: combinedHTML });
  } catch (error) {
    console.error("Cross-selling generation failed", error);
    FfEvents.emit("crossSelling:error", { error });
  }
}

// --- Perform helper ---
async function perform(fn, operation, context = {}) {
  try {
    FfEvents.emit("cart:loading", { operation, ...context });
    const result = await fn();
    FfEvents.emit("cart:updated", { cart: result, operation, ...context });
    return result;
  } catch (error) {
    FfEvents.emit("cart:error", { error, operation, ...context });
    throw error;
  }
}

// --- Public API ---
const FfCartService = {
  add: async (payload, context = {}) =>
    perform(async () => await ffDebouncedAdd(payload), "add", context),
  update: async (payload, context = {}) =>
    perform(async () => await ffDebouncedUpdate(payload), "update", context),
  clear: async (context = {}) =>
    perform(() => ffDebouncedClear(), "clear", context),
  fetch: _fetch,
  ffGenerateCrossSelling,
};

const ffListeners = {};

const FfEvents = {
  on(event, fn) {
    if (!ffListeners[event]) ffListeners[event] = [];
    ffListeners[event].push(fn);
  },
  off(event, fn) {
    if (!ffListeners[event]) return;
    ffListeners[event] = ffListeners[event].filter((f) => f !== fn);
  },
  emit(event, payload) {
    if (!ffListeners[event]) return;
    ffListeners[event].forEach((fn) => fn(payload));
  },
};

// --------------------------------------- UI LIFECYCLE ---------------------------------------
(() => {
  const productIdArray = [
    ...document.querySelectorAll(".ff-cart-line-item--js"),
  ].map((item) => item.dataset.ffItemId);
  if (productIdArray.length > 0) {
    FfCartService.ffGenerateCrossSelling({
      productIdArray: productIdArray,
      sectionId: ffCartSectionsMap.crossSellingApiId.sectionApiId,
      limit: 6,
      intent: "related",
    });
  }
})();

FfEvents.on("cart:loading", ({ operation, source }) => {
  source?.ffCartLoading?.(operation);
});

FfEvents.on("cart:updated", ({ cart, operation, source }) => {
  source?.ffCartUpdated?.(cart, operation);
});

FfEvents.on("cart:error", ({ error, operation, source }) => {
  source?.ffCartError?.(error, operation);
});

// --- CART UPDATED ---
FfEvents.on("cart:updated", async ({ cart }) => {
  // Update all cart sections
  ffCartSectionsMap.cartApiIds.forEach(
    ({ sectionApiId, selectors, onUpdate }) => {
      const html = cart.sections?.[sectionApiId];
      const temp = document.createElement("div");
      temp.innerHTML = html;

      selectors.forEach((selector) => {
        const newEl = document.querySelector(selector);
        newEl.innerHTML = temp.querySelector(selector).innerHTML;
        newEl.querySelectorAll("script").forEach((script) => {
          const newScript = document.createElement("script");
          newScript.innerHTML = script.innerHTML;
          script.replaceWith(newScript);
        });
        ffAttachEvents(newEl);
      });
      
      // Reinitialize cart drawer togglers if this is the cart drawer section
      if (sectionApiId === 'ff-cart-drawer') {
        const cartDrawer = document.querySelector('.ff-cart-drawer');
        if (cartDrawer && cartDrawer.ffInit) {
          cartDrawer.ffInit(true);
        }
      }
    }
  );

  // Update cross-selling sections
  const productIds = (cart?.items || []).map((item) => item.product_id);
  if (productIds.length > 0) {
    FfCartService.ffGenerateCrossSelling({
      productIdArray: productIds,
      sectionId: ffCartSectionsMap.crossSellingApiId.sectionApiId,
      limit: 6,
      intent: "related",
    });
  }

  setTimeout(() => {
    document.body.classList.remove("ff-cart-loading");
  }, 1000);
});

// --- CROSS-SELLING GENERATED ---
FfEvents.on("crossSelling:generated", ({ html }) => {
  const crossSellingEl = document.querySelector(
    ffCartSectionsMap.crossSellingApiId.selector
  );
  if (!crossSellingEl) return;
  ffRenderCrossSelling(crossSellingEl, html);
  ffAttachEvents(crossSellingEl);
});

FfEvents.on("cart:loading", () => {
  document.body.classList.add("ff-cart-loading");
});

FfEvents.on("cart:error", ({ error }) => {
  // document.body.classList.remove("ff-cart-loading");
  console.error("Cart error:", error);
});

FfEvents.on("crossSelling:error", ({ error }) => {
  console.error("Cross-selling failed", error);
});

/**
 * Attach all event listeners inside the container element.
 */
function ffAttachEvents(containerEl) {
  // ADD TO CART
  containerEl.querySelectorAll(".ff-cart-add--js").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Find form - either parent or sibling
      let form = btn.closest("form");

      await FfCartService.add(form);
    });
  });

  // REMOVE ITEM
  containerEl.querySelectorAll(".ff-cart-remove--js").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.preventDefault();
      const key = btn.dataset.ffCartItemKey;
      if (!key) return;

      await FfCartService.update({ updates: { [key.toString()]: 0 } });
    });
  });

  // UPDATE QTY
  containerEl.querySelectorAll(".ff-cart-qty--js").forEach((input) => {
    input.addEventListener("change", async (e) => {
      const key = input.dataset.ffCartItemKey;
      const qty = parseInt(e.target.value);

      input.closest(".ff-cart-qty_container--js")?.classList.add("ff-item-quantity--updating");

      await FfCartService.update({ updates: { [key.toString()]: qty } });
    });
  });

  // DECREASE QTY
  containerEl
    .querySelectorAll(".ff-cart-drawer_qty-decrease--js")
    .forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        const key = btn.dataset.ffCartItemKey;

        const input = containerEl.querySelector(
          `input[data-ff-cart-item-key="${key}"]`
        );

        input.closest(".ff-cart-qty_container--js")?.classList.add("ff-item-quantity--updating");

        const currentQty = parseInt(input.value);
        const newQty = Math.max(0, currentQty - 1);

        await FfCartService.update({ updates: { [key.toString()]: newQty } });
      });
    });

  // INCREASE QTY
  containerEl
    .querySelectorAll(".ff-cart-drawer_qty-increase--js")
    .forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        const key = btn.dataset.ffCartItemKey;

        const input = containerEl.querySelector(
          `input[data-ff-cart-item-key="${key}"]`
        );

        input.closest(".ff-cart-qty_container--js")?.classList.add("ff-item-quantity--updating");

        const currentQty = parseInt(input.value);
        const newQty = currentQty + 1;

        await FfCartService.update({ updates: { [key.toString()]: newQty } });
      });
    });
}

function ffRenderCart(target, newHtml) {
  const temp = document.createElement("div");
  temp.innerHTML = newHtml;

  target
    .querySelector(".ff-cart-drawer_content--js")
    .replaceWith(temp.querySelector(".ff-cart-drawer_content--js"));
  ffAttachEvents(target);
}

function ffRenderCrossSelling(target, crossSellingHtml) {
  const temp = document.createElement("div");
  temp.innerHTML = crossSellingHtml;

  const cartItemIds = new Set();

  // Filter out repeated recommendations
  const allProductCards = temp.querySelectorAll(
    ".ff-card-product-cross-selling"
  );

  if (allProductCards.length === 0) {
    document.querySelector(".ff-cross-selling-container--js").remove();
    return;
  }

  const productCards = Array.from(allProductCards).filter((card) => {
    const itemId = card.dataset.ffItemId;
    if (!cartItemIds.has(itemId)) {
      cartItemIds.add(itemId);
      return true;
    }
  });

  const productSlots = target.querySelectorAll(".ff-cross-selling_slot");
  const lastSlot = productSlots[productSlots.length - 1];

  productCards.forEach((productCard, idx) => {
    if (productSlots[idx]) {
      // Remove skeleton and add product card
      productSlots[idx].innerHTML = "";
      productSlots[idx].appendChild(productCard);
    } else {
      const newSlot = productSlots[0].cloneNode(true);
      newSlot.innerHTML = productCard.outerHTML;
      lastSlot.after(newSlot);
    }
  });

  // Hide remaining slots that don't have products
  productSlots.forEach((slot, idx) => {
    if (idx >= productCards.length) {
      slot.style.display = "none";
    }
  });

  if (productCards.length === 0) {
    target.style.display = "none";
  }
}
